import type { AxiosError } from 'axios';

import type {
  NotificationBodyUrlsDto,
  NotificationMessageDto,
  NotificationMessageUpsertDto,
  NotificationTargetDto,
  SetNotificationTargetsDto,
  // NotificationTargetDto,
  // SetNotificationTargetsDto,
} from '@/models/VnMessageService';
import { NotificationKind, VnMessageServiceApi as api } from '@/models/VnMessageService';

import { messageServiceAxios } from './interceptor';
import { LocalStorageService } from './local-storage';

const targetNotificationsInFlight = new Map<string, Promise<NotificationMessageDto[]>>();

/** Local-storage key holding the per-OU map of read notification ids. */
const READ_STATE_KEY = LocalStorageService.LocalStorageItems.VNMessages;

/** Reads the full `{ [ouId]: readNotificationIds[] }` map from local storage. */
function getReadByOuMap(): Record<string, string[]> {
  return LocalStorageService.getItem(READ_STATE_KEY) ?? {};
}

/** Persists the read notification ids for a single OU. */
function setReadForOu(ouId: string, ids: string[]): void {
  const map = getReadByOuMap();
  map[ouId] = ids;
  LocalStorageService.setItem(READ_STATE_KEY, map);
}

/**
 * Base error for every failure surfaced by `VnMessageService`. All service methods reject with an
 * instance of this class (or a subclass), so callers can rely on `instanceof VnNotificationError`
 * and inspect `status` instead of unpacking raw axios errors.
 */
export class VnNotificationError extends Error {
  /** HTTP status code when the failure originated from an HTTP response; undefined otherwise. */
  readonly status?: number;

  /** The original error (axios error, thrown value, etc.) for debugging/logging. */
  readonly cause?: unknown;

  constructor(message: string, status?: number, cause?: unknown) {
    super(message);
    this.name = 'VnNotificationError';
    this.status = status;
    this.cause = cause;
  }
}

/** Thrown by client-side validation before a request is issued (no HTTP round-trip). */
export class VnNotificationValidationError extends VnNotificationError {
  constructor(message: string, cause?: unknown) {
    super(message, undefined, cause);
    this.name = 'VnNotificationValidationError';
  }
}

/** Maps a `400 Bad Request` response. */
export class VnNotificationBadRequestError extends VnNotificationError {
  constructor(message: string, cause?: unknown) {
    super(message, 400, cause);
    this.name = 'VnNotificationBadRequestError';
  }
}

/** Maps a `404 Not Found` response. */
export class VnNotificationNotFoundError extends VnNotificationError {
  constructor(message: string, cause?: unknown) {
    super(message, 404, cause);
    this.name = 'VnNotificationNotFoundError';
  }
}

/** Maps a `500 Internal Server Error` response. */
export class VnNotificationServerError extends VnNotificationError {
  constructor(message: string, cause?: unknown) {
    super(message, 500, cause);
    this.name = 'VnNotificationServerError';
  }
}

/** Extracts a human-readable message from an axios error's response body (string or common shapes). */
function extractResponseMessage(err: AxiosError): string | undefined {
  const data = err?.response?.data as unknown;
  if (data == null) {
    return undefined;
  }
  if (typeof data === 'string') {
    return data.trim() === '' ? undefined : data;
  }
  const structured = data as { message?: string; Message?: string; InnerException?: { Message?: string } };
  return structured.InnerException?.Message ?? structured.Message ?? structured.message ?? undefined;
}

/**
 * Centralized status-code mapping: converts an unknown thrown value (typically an axios error) into a
 * typed `VnNotificationError`. `400/404/500` map to their dedicated subclasses; anything else falls
 * back to the base error while preserving whatever status/message is available.
 */
function toTypedNotificationError(err: unknown): VnNotificationError {
  if (err instanceof VnNotificationError) {
    return err;
  }

  const axiosErr = err as AxiosError;
  const status = axiosErr?.response?.status;
  const serverMessage = extractResponseMessage(axiosErr);

  switch (status) {
    case 400:
      return new VnNotificationBadRequestError(serverMessage ?? 'The notification request was invalid.', err);
    case 404:
      return new VnNotificationNotFoundError(serverMessage ?? 'The requested notification was not found.', err);
    case 500:
      return new VnNotificationServerError(
        serverMessage ?? 'The notification service encountered an unexpected error.',
        err
      );
    default:
      return new VnNotificationError(
        serverMessage ?? axiosErr?.message ?? 'An unexpected notification service error occurred.',
        status,
        err
      );
  }
}

/** Runs a service call and normalizes any failure into a typed `VnNotificationError`. */
async function run<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    throw toTypedNotificationError(err);
  }
}

async function hydrateNotificationBody(notification: NotificationMessageDto): Promise<NotificationMessageDto> {
  if (notification.body != null && notification.body !== '') {
    return notification;
  }

  try {
    const res = await messageServiceAxios.get(api.getBodyUrls(notification.id));
    const bodyUrls: NotificationBodyUrlsDto = res.data;
    if (!bodyUrls?.readUrl) {
      return notification;
    }

    const response = await fetch(bodyUrls.readUrl, { cache: 'no-cache' });
    if (!response.ok) {
      return notification;
    }

    const body = await response.text();
    return { ...notification, body };
  } catch {
    return notification;
  }
}

function populateNotificationBody(notification: NotificationMessageDto): Promise<NotificationMessageDto>;
function populateNotificationBody(notifications: NotificationMessageDto[]): Promise<NotificationMessageDto[]>;
function populateNotificationBody(
  input: NotificationMessageDto | NotificationMessageDto[]
): Promise<NotificationMessageDto | NotificationMessageDto[]> {
  if (Array.isArray(input)) {
    return Promise.all(input.map((notification) => hydrateNotificationBody(notification)));
  }
  return hydrateNotificationBody(input);
}

/**
 * Minimal client-side validation applied before `POST /notification`:
 * - `ownerId` and `title` are required.
 * - `severity` is required when `kind === NotificationKind.Alert`.
 *
 * Throws `VnNotificationValidationError` (before any network call) when the payload is invalid.
 */
function validateUpsertPayload(payload: NotificationMessageUpsertDto): void {
  const errors: string[] = [];

  if (!payload?.ownerId || payload.ownerId.trim() === '') {
    errors.push('ownerId is required.');
  }
  if (!payload?.title || payload.title.trim() === '') {
    errors.push('title is required.');
  }
  if (payload?.kind === NotificationKind.Alert && !payload.severity) {
    errors.push('severity is required when kind is "Alert".');
  }

  if (errors.length > 0) {
    throw new VnNotificationValidationError(errors.join(' '));
  }
}

const VnMessageService = {

  populateNotificationBody,

  async upsertNotification(payload: NotificationMessageUpsertDto): Promise<NotificationMessageDto> {
    validateUpsertPayload(payload);
    return run(async () => {
      const res = await messageServiceAxios.post(api.upsertNotification, payload);
      return res.data;
    });
  },

  async getNotification(notificationId: string): Promise<NotificationMessageDto> {
    return run(async () => {
      const res = await messageServiceAxios.get(api.getNotification(notificationId));
      return res.data;
    });
  },

  async deleteNotification(notificationId: string): Promise<void> {
    return run(async () => {
      await messageServiceAxios.delete(api.deleteNotification(notificationId));
    });
  },

  async listNotificationsByOwner(ownerId: string): Promise<NotificationMessageDto[]> {
    return run(async () => {
      const res = await messageServiceAxios.get(api.listByOwner(ownerId));
      return res.data;
    });
  },

  async listNotificationsByTarget(targetId: string): Promise<NotificationMessageDto[]> {
    const inFlight = targetNotificationsInFlight.get(targetId);
    if (inFlight) {
      return inFlight;
    }

    const request = run(async () => {
      const res = await messageServiceAxios.get(api.getUserNotifications(targetId));
      const notifications: NotificationMessageDto[] = res.data;
      return notifications;
    }).finally(() => {
      targetNotificationsInFlight.delete(targetId);
    });

    targetNotificationsInFlight.set(targetId, request);
    return request;
  },

  async getNotificationBodyUrls(notificationId: string): Promise<NotificationBodyUrlsDto> {
    return run(async () => {
      const res = await messageServiceAxios.get(api.getBodyUrls(notificationId));
      return res.data;
    });
  },

  async getNotificationTargets(notificationId: string): Promise<NotificationTargetDto[]> {
    return run(async () => {
      const res = await messageServiceAxios.get(api.listTargets(notificationId));
      return res.data;
    });
  },

  async setNotificationTargets(
    notificationId: string,
    payload: SetNotificationTargetsDto
  ): Promise<NotificationTargetDto[]> {
    return run(async () => {
      const res = await messageServiceAxios.put(api.setTargets(notificationId), payload);
      return res.data;
    });
  },

  async addNotificationTarget(notificationId: string, targetId: string): Promise<NotificationTargetDto> {
    return run(async () => {
      const res = await messageServiceAxios.post(api.addTarget(notificationId, targetId));
      return res.data;
    });
  },

  async removeNotificationTarget(notificationId: string, targetId: string): Promise<void> {
    return run(async () => {
      await messageServiceAxios.delete(api.removeTarget(notificationId, targetId));
    });
  },

  // #region user state in local storage
  getRead(ouId: string | undefined): string[] {
    if (!ouId) {
      return [];
    }
    return getReadByOuMap()[ouId] ?? [];
  },

  markAsRead(ouId: string | undefined, notificationId: string): void {
    if (!ouId) {
      return;
    }
    const read = this.getRead(ouId);
    if (!read.includes(notificationId)) {
      setReadForOu(ouId, [...read, notificationId]);
    }
  },

  markAsUnread(ouId: string | undefined, notificationId: string): void {
    if (!ouId) {
      return;
    }
    const read = this.getRead(ouId);
    if (!read.includes(notificationId)) {
      return;
    }
    setReadForOu(
      ouId,
      read.filter((id) => id !== notificationId)
    );
  },

  cleanUpExpiredNotifications(ouId: string | undefined, notifications: NotificationMessageDto[]): void {
    if (!ouId) {
      return;
    }
    const read = this.getRead(ouId);
    const activeIds = new Set(notifications.map((n) => n.id));
    const pruned = read.filter((id) => activeIds.has(id));
    if (pruned.length !== read.length) {
      setReadForOu(ouId, pruned);
    }
  },
  // #endregion
};

export { VnMessageService };
